import { createTheme } from "@mui/material/styles"

// Base colors
const colors = {
  white: "#ffffff",
  black: "#000000",
  gray50: "#f9fafb",
  gray100: "#f3f4f6",
  gray200: "#e5e7eb",
  gray300: "#d1d5db",
  gray400: "#9ca3af",
  gray500: "#6b7280",
  gray600: "#4b5563",
  gray700: "#374151",
  gray800: "#1f2937",
  gray900: "#111827",
}

// ERP Table Colors - for consistent styling across tables
export const erpTableColors = {
  products: {
    headerBg: "#eff6ff",
    headerBorder: "#bfdbfe",
    headerText: "#1e40af",
  },
  loans: {
    headerBg: "#f5f3ff",
    headerBorder: "#c4b5fd",
    headerText: "#5b21b6",
  },
  default: {
    headerBg: "#f9fafb",
    headerBorder: "#e5e7eb",
    headerText: "#6b7280",
  },
  row: {
    hoverBg: "#f9fafb",
    borderColor: "#f3f4f6",
  },
}

// Shared typography
const typography = {
  fontFamily: '"Inter", "Poppins", sans-serif',
  fontSize: 13,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 600,
  h1: { fontSize: "2rem", fontWeight: 600, lineHeight: 1.2 },
  h2: { fontSize: "1.5rem", fontWeight: 600, lineHeight: 1.2 },
  h3: { fontSize: "1.25rem", fontWeight: 600, lineHeight: 1.2 },
  h4: { fontSize: "1.125rem", fontWeight: 600, lineHeight: 1.2 },
  h5: { fontSize: "1rem", fontWeight: 600, lineHeight: 1.2 },
  h6: { fontSize: "0.875rem", fontWeight: 600, lineHeight: 1.2 },
  subtitle1: { fontSize: "0.875rem", fontWeight: 500 },
  subtitle2: { fontSize: "0.8125rem", fontWeight: 500 },
  body1: { fontSize: "0.8125rem", fontWeight: 400 },
  body2: { fontSize: "0.75rem", fontWeight: 400 },
  button: { textTransform: "none", fontWeight: 500 },
  caption: { fontSize: "0.6875rem" },
  overline: { fontSize: "0.625rem", textTransform: "uppercase", letterSpacing: "0.1em" },
}

// =============================================================================
// ERP MINIMAL THEME - Clean, monochrome, professional
// =============================================================================
export const erpMinimalTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: colors.gray800,
      light: colors.gray600,
      dark: colors.gray900,
      contrastText: colors.white,
    },
    secondary: {
      main: colors.gray500,
      light: colors.gray400,
      dark: colors.gray600,
      contrastText: colors.white,
    },
    success: {
      main: "#059669",
      light: "#d1fae5",
      dark: "#047857",
      contrastText: colors.white,
    },
    info: {
      main: colors.gray600,
      light: colors.gray100,
      dark: colors.gray700,
      contrastText: colors.white,
    },
    warning: {
      main: "#d97706",
      light: "#fef3c7",
      dark: "#b45309",
      contrastText: colors.white,
    },
    error: {
      main: "#dc2626",
      light: "#fee2e2",
      dark: "#b91c1c",
      contrastText: colors.white,
    },
    background: {
      default: "#f8fafc",
      paper: colors.white,
    },
    text: {
      primary: colors.gray900,
      secondary: colors.gray500,
      disabled: colors.gray400,
    },
    divider: colors.gray200,
  },
  typography,
  shape: { borderRadius: 6 },
  shadows: [
    "none",
    "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    "0 1px 3px 0 rgb(0 0 0 / 0.1)",
    "0 4px 6px -1px rgb(0 0 0 / 0.1)",
    "0 10px 15px -3px rgb(0 0 0 / 0.1)",
    ...Array(20).fill("0 10px 15px -3px rgb(0 0 0 / 0.1)"),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: "#f8fafc" },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          borderRadius: 6,
          padding: "8px 16px",
        },
        contained: {
          boxShadow: "none",
          "&:hover": { boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)" },
        },
        outlined: {
          borderColor: colors.gray300,
          "&:hover": { borderColor: colors.gray400, backgroundColor: colors.gray50 },
        },
      },
      defaultProps: { disableElevation: true },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          border: `1px solid ${colors.gray200}`,
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
        elevation1: { boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)" },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500 },
        filled: { backgroundColor: colors.gray100, color: colors.gray700 },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: colors.gray200 },
        head: {
          fontWeight: 600,
          backgroundColor: colors.gray50,
          color: colors.gray600,
          textTransform: "uppercase",
          fontSize: "0.6875rem",
          letterSpacing: "0.05em",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.white,
          color: colors.gray900,
          boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.white,
          borderRight: `1px solid ${colors.gray200}`,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          margin: "2px 8px",
          "&.Mui-selected": {
            backgroundColor: colors.gray100,
            color: colors.gray900,
            "&:hover": { backgroundColor: colors.gray200 },
          },
          "&:hover": { backgroundColor: colors.gray50 },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: `1px solid ${colors.gray200}`,
          borderRadius: 8,
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.gray50,
            borderBottom: `1px solid ${colors.gray200}`,
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 600,
            fontSize: "0.6875rem",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: colors.gray600,
          },
          "& .MuiDataGrid-row:hover": { backgroundColor: colors.gray50 },
          "& .MuiDataGrid-cell": { borderColor: colors.gray100 },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 6,
            "& fieldset": { borderColor: colors.gray300 },
            "&:hover fieldset": { borderColor: colors.gray400 },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 12 },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8 },
        standardSuccess: { backgroundColor: "#ecfdf5", color: "#065f46" },
        standardError: { backgroundColor: "#fef2f2", color: "#991b1b" },
        standardWarning: { backgroundColor: "#fffbeb", color: "#92400e" },
        standardInfo: { backgroundColor: colors.gray100, color: colors.gray700 },
      },
    },
  },
})

// =============================================================================
// ERP COLOR THEME - Vibrant, modern, colorful
// =============================================================================
export const erpColorTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#3b82f6", // Blue
      light: "#60a5fa",
      dark: "#2563eb",
      contrastText: colors.white,
    },
    secondary: {
      main: "#8b5cf6", // Purple
      light: "#a78bfa",
      dark: "#7c3aed",
      contrastText: colors.white,
    },
    success: {
      main: "#10b981", // Emerald
      light: "#d1fae5",
      dark: "#059669",
      contrastText: colors.white,
    },
    info: {
      main: "#06b6d4", // Cyan
      light: "#cffafe",
      dark: "#0891b2",
      contrastText: colors.white,
    },
    warning: {
      main: "#f59e0b", // Amber
      light: "#fef3c7",
      dark: "#d97706",
      contrastText: colors.black,
    },
    error: {
      main: "#ef4444", // Red
      light: "#fee2e2",
      dark: "#dc2626",
      contrastText: colors.white,
    },
    background: {
      default: "#f0f9ff", // Light blue tint
      paper: colors.white,
    },
    text: {
      primary: colors.gray900,
      secondary: colors.gray500,
      disabled: colors.gray400,
    },
    divider: "#e0f2fe",
  },
  typography,
  shape: { borderRadius: 8 },
  shadows: [
    "none",
    "0 1px 3px 0 rgb(59 130 246 / 0.1)",
    "0 4px 6px -1px rgb(59 130 246 / 0.1)",
    "0 10px 15px -3px rgb(59 130 246 / 0.1)",
    "0 20px 25px -5px rgb(59 130 246 / 0.1)",
    ...Array(20).fill("0 20px 25px -5px rgb(59 130 246 / 0.1)"),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: "#f0f9ff" },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          borderRadius: 8,
          padding: "8px 16px",
        },
        contained: {
          boxShadow: "0 1px 3px 0 rgb(59 130 246 / 0.3)",
          "&:hover": { boxShadow: "0 4px 6px -1px rgb(59 130 246 / 0.3)" },
        },
        containedPrimary: {
          background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
          "&:hover": { background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)" },
        },
        containedSecondary: {
          background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
          "&:hover": { background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)" },
        },
        containedSuccess: {
          background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
          "&:hover": { background: "linear-gradient(135deg, #059669 0%, #047857 100%)" },
        },
        containedError: {
          background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
          "&:hover": { background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)" },
        },
        outlined: {
          borderColor: "#bfdbfe",
          "&:hover": { borderColor: "#3b82f6", backgroundColor: "#eff6ff" },
        },
      },
      defaultProps: { disableElevation: true },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 3px 0 rgb(59 130 246 / 0.1)",
          border: "1px solid #e0f2fe",
          borderRadius: 12,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
        elevation1: { boxShadow: "0 1px 3px 0 rgb(59 130 246 / 0.1)" },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500, borderRadius: 6 },
        filled: { backgroundColor: "#dbeafe", color: "#1e40af" },
        colorPrimary: { backgroundColor: "#dbeafe", color: "#1e40af" },
        colorSecondary: { backgroundColor: "#ede9fe", color: "#5b21b6" },
        colorSuccess: { backgroundColor: "#d1fae5", color: "#065f46" },
        colorError: { backgroundColor: "#fee2e2", color: "#991b1b" },
        colorWarning: { backgroundColor: "#fef3c7", color: "#92400e" },
        colorInfo: { backgroundColor: "#cffafe", color: "#155e75" },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: "#e0f2fe" },
        head: {
          fontWeight: 600,
          backgroundColor: "#eff6ff",
          color: "#1e40af",
          textTransform: "uppercase",
          fontSize: "0.6875rem",
          letterSpacing: "0.05em",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.white,
          color: colors.gray900,
          boxShadow: "0 1px 3px 0 rgb(59 130 246 / 0.1)",
          borderBottom: "1px solid #e0f2fe",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.white,
          borderRight: "1px solid #e0f2fe",
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: "2px 8px",
          "&.Mui-selected": {
            backgroundColor: "#dbeafe",
            color: "#1e40af",
            "&:hover": { backgroundColor: "#bfdbfe" },
          },
          "&:hover": { backgroundColor: "#eff6ff" },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: "1px solid #e0f2fe",
          borderRadius: 12,
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#eff6ff",
            borderBottom: "1px solid #bfdbfe",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 600,
            fontSize: "0.6875rem",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: "#1e40af",
          },
          "& .MuiDataGrid-row:hover": { backgroundColor: "#eff6ff" },
          "& .MuiDataGrid-cell": { borderColor: "#e0f2fe" },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            "& fieldset": { borderColor: "#bfdbfe" },
            "&:hover fieldset": { borderColor: "#3b82f6" },
            "&.Mui-focused fieldset": { borderColor: "#3b82f6" },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 16 },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10 },
        standardSuccess: {
          backgroundColor: "#ecfdf5",
          color: "#065f46",
          border: "1px solid #a7f3d0",
        },
        standardError: {
          backgroundColor: "#fef2f2",
          color: "#991b1b",
          border: "1px solid #fecaca",
        },
        standardWarning: {
          backgroundColor: "#fffbeb",
          color: "#92400e",
          border: "1px solid #fde68a",
        },
        standardInfo: {
          backgroundColor: "#eff6ff",
          color: "#1e40af",
          border: "1px solid #bfdbfe",
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        colorDefault: {
          backgroundColor: "#3b82f6",
          color: colors.white,
        },
      },
    },
  },
})

// =============================================================================
// ERP ENTERPRISE THEME - Dense, compact, serious (SAP/Oracle/Dynamics style)
// =============================================================================

const steel = {
  50: "#f8fafc",
  100: "#f1f5f9",
  200: "#e2e8f0",
  300: "#cbd5e1",
  400: "#94a3b8",
  500: "#64748b",
  600: "#475569",
  700: "#334155",
  800: "#1e293b",
  900: "#0f172a",
}

export const erpEnterpriseTableColors = {
  products: { headerBg: steel[100], headerBorder: steel[300], headerText: steel[700] },
  loans: { headerBg: steel[100], headerBorder: steel[300], headerText: steel[700] },
  default: { headerBg: steel[100], headerBorder: steel[300], headerText: steel[600] },
  row: { hoverBg: steel[50], borderColor: steel[200] },
}

export const erpEnterpriseTheme = createTheme({
  palette: {
    mode: "light",
    primary: { main: steel[700], light: steel[600], dark: steel[800], contrastText: "#ffffff" },
    secondary: { main: steel[500], light: steel[400], dark: steel[600], contrastText: "#ffffff" },
    success: { main: "#15803d", light: "#dcfce7", dark: "#166534", contrastText: "#ffffff" },
    info: { main: "#0369a1", light: "#e0f2fe", dark: "#075985", contrastText: "#ffffff" },
    warning: { main: "#b45309", light: "#fef3c7", dark: "#92400e", contrastText: "#ffffff" },
    error: { main: "#b91c1c", light: "#fee2e2", dark: "#991b1b", contrastText: "#ffffff" },
    background: { default: steel[100], paper: "#ffffff" },
    text: { primary: steel[900], secondary: steel[500], disabled: steel[400] },
    divider: steel[200],
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: 12,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    h1: { fontSize: "1.625rem", fontWeight: 600, lineHeight: 1.15, letterSpacing: "-0.01em" },
    h2: { fontSize: "1.25rem", fontWeight: 600, lineHeight: 1.15, letterSpacing: "-0.01em" },
    h3: { fontSize: "1.0625rem", fontWeight: 600, lineHeight: 1.2 },
    h4: { fontSize: "0.9375rem", fontWeight: 600, lineHeight: 1.2 },
    h5: { fontSize: "0.8125rem", fontWeight: 600, lineHeight: 1.2 },
    h6: { fontSize: "0.75rem", fontWeight: 600, lineHeight: 1.2 },
    subtitle1: { fontSize: "0.8125rem", fontWeight: 500, lineHeight: 1.3 },
    subtitle2: { fontSize: "0.75rem", fontWeight: 500, lineHeight: 1.3 },
    body1: { fontSize: "0.75rem", fontWeight: 400, lineHeight: 1.4 },
    body2: { fontSize: "0.6875rem", fontWeight: 400, lineHeight: 1.4 },
    button: { textTransform: "none", fontWeight: 500, fontSize: "0.75rem" },
    caption: { fontSize: "0.625rem", lineHeight: 1.3 },
    overline: { fontSize: "0.5625rem", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 },
  },
  shape: { borderRadius: 3 },
  shadows: [
    "none",
    "0 1px 2px 0 rgb(0 0 0 / 0.03)",
    "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    "0 2px 4px 0 rgb(0 0 0 / 0.05)",
    "0 2px 4px 0 rgb(0 0 0 / 0.06)",
    ...Array(20).fill("0 2px 4px 0 rgb(0 0 0 / 0.06)"),
  ],
  // Custom layout dimensions for enterprise density
  layoutConfig: {
    drawerWidth: 220,
    headerHeight: 44,
    contentPadding: 1.5,
    contentPaddingTop: 1,
    sidebarLogoHeight: 44,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: { body: { backgroundColor: steel[100] } },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          borderRadius: 3,
          padding: "4px 12px",
          fontSize: "0.75rem",
          minHeight: 30,
        },
        sizeSmall: { padding: "3px 10px", fontSize: "0.6875rem", minHeight: 26 },
        contained: { boxShadow: "none", "&:hover": { boxShadow: "none" } },
        outlined: {
          borderColor: steel[300],
          "&:hover": { borderColor: steel[400], backgroundColor: steel[50] },
        },
      },
      defaultProps: { disableElevation: true },
    },
    MuiCard: {
      styleOverrides: {
        root: { boxShadow: "none", border: `1px solid ${steel[200]}`, borderRadius: 3 },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
        elevation1: { boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.03)" },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500, borderRadius: 3, height: 22 },
        sizeSmall: { height: 20, fontSize: "0.625rem" },
        filled: { backgroundColor: steel[200], color: steel[700] },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: steel[200], padding: "6px 8px", fontSize: "0.75rem" },
        head: {
          fontWeight: 600,
          backgroundColor: steel[100],
          color: steel[600],
          textTransform: "uppercase",
          fontSize: "0.625rem",
          letterSpacing: "0.05em",
          padding: "6px 8px",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
          color: steel[900],
          boxShadow: "none",
          borderBottom: `1px solid ${steel[200]}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundColor: "#ffffff", borderRight: `1px solid ${steel[200]}` },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          margin: "1px 4px",
          minHeight: 32,
          padding: "4px 8px",
          "&.Mui-selected": {
            backgroundColor: steel[200],
            color: steel[900],
            "&:hover": { backgroundColor: steel[300] },
          },
          "&:hover": { backgroundColor: steel[100] },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: { root: { minWidth: 28 } },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: `1px solid ${steel[200]}`,
          borderRadius: 3,
          fontSize: "0.75rem",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: steel[100],
            borderBottom: `1px solid ${steel[200]}`,
            minHeight: "32px !important",
            maxHeight: "32px !important",
          },
          "& .MuiDataGrid-columnHeader": { height: "32px !important" },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 600,
            fontSize: "0.625rem",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: steel[600],
          },
          "& .MuiDataGrid-row": {
            minHeight: "32px !important",
            maxHeight: "32px !important",
          },
          "& .MuiDataGrid-row:hover": { backgroundColor: steel[50] },
          "& .MuiDataGrid-cell": {
            borderColor: steel[100],
            padding: "4px 8px",
            display: "flex",
            alignItems: "center",
          },
          "& .MuiDataGrid-footerContainer": {
            minHeight: 36,
            borderTop: `1px solid ${steel[200]}`,
          },
        },
      },
      defaultProps: { density: "compact", rowHeight: 32, columnHeaderHeight: 32 },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 3,
            fontSize: "0.75rem",
            "& fieldset": { borderColor: steel[300] },
            "&:hover fieldset": { borderColor: steel[400] },
          },
          "& .MuiOutlinedInput-input": { padding: "6px 10px" },
        },
      },
      defaultProps: { size: "small" },
    },
    MuiInputBase: {
      styleOverrides: {
        sizeSmall: { fontSize: "0.75rem" },
      },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 6 } },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 3, padding: "4px 12px" },
        standardSuccess: { backgroundColor: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0" },
        standardError: { backgroundColor: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca" },
        standardWarning: { backgroundColor: "#fffbeb", color: "#92400e", border: "1px solid #fde68a" },
        standardInfo: { backgroundColor: steel[100], color: steel[700], border: `1px solid ${steel[300]}` },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { width: 26, height: 26, fontSize: "0.75rem" },
        colorDefault: { backgroundColor: steel[700], color: "#ffffff" },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: "none", minHeight: 36, padding: "6px 12px", fontSize: "0.75rem", fontWeight: 500 },
      },
    },
    MuiTabs: {
      styleOverrides: { root: { minHeight: 36 } },
    },
    MuiToolbar: {
      styleOverrides: { root: { minHeight: "44px !important" } },
    },
    MuiIconButton: {
      styleOverrides: { sizeSmall: { padding: 4 } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { fontSize: "0.6875rem", borderRadius: 3, padding: "4px 8px" },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { borderRadius: 4, border: `1px solid ${steel[200]}` },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { fontSize: "0.75rem", minHeight: 32, padding: "4px 12px" },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: steel[200] } },
    },
    MuiSelect: {
      styleOverrides: {
        select: { padding: "6px 10px", fontSize: "0.75rem" },
      },
    },
  },
})

// =============================================================================
// ELITON THEME - Branded, compact, user-friendly ERP
// =============================================================================

const eliton = {
  red: "#e50019",
  redDark: "#c40016",
  redDeep: "#9e0012",
  dark: "#212121",
  darkLight: "#2c2c2c",
  dark600: "#333333",
  dark500: "#424242",
  dark400: "#616161",
  dark300: "#757575",
  dark200: "#9e9e9e",
  dark100: "#bdbdbd",
  gray50: "#fafafa",
  gray100: "#f5f5f5",
  gray200: "#eeeeee",
  gray300: "#e0e0e0",
  white: "#ffffff",
}

export const erpElitonTableColors = {
  products: { headerBg: "#fef2f2", headerBorder: "#fecaca", headerText: "#991b1b" },
  loans: { headerBg: "#f5f5f5", headerBorder: "#e0e0e0", headerText: "#424242" },
  default: { headerBg: eliton.gray100, headerBorder: eliton.gray300, headerText: eliton.dark500 },
  row: { hoverBg: eliton.gray50, borderColor: eliton.gray200 },
}

export const erpElitonTheme = createTheme({
  palette: {
    mode: "light",
    primary: { main: eliton.red, light: "#ff4d4d", dark: eliton.redDark, contrastText: eliton.white },
    secondary: { main: eliton.dark, light: eliton.dark500, dark: "#111111", contrastText: eliton.white },
    success: { main: "#2e7d32", light: "#e8f5e9", dark: "#1b5e20", contrastText: eliton.white },
    info: { main: "#0277bd", light: "#e1f5fe", dark: "#01579b", contrastText: eliton.white },
    warning: { main: "#ef6c00", light: "#fff3e0", dark: "#e65100", contrastText: eliton.white },
    error: { main: "#c62828", light: "#ffebee", dark: "#b71c1c", contrastText: eliton.white },
    background: { default: eliton.gray100, paper: eliton.white },
    text: { primary: eliton.dark, secondary: eliton.dark400, disabled: eliton.dark200 },
    divider: eliton.gray300,
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: 12.5,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    h1: { fontSize: "1.5rem", fontWeight: 700, lineHeight: 1.2, letterSpacing: "-0.01em" },
    h2: { fontSize: "1.25rem", fontWeight: 700, lineHeight: 1.2 },
    h3: { fontSize: "1.0625rem", fontWeight: 600, lineHeight: 1.25 },
    h4: { fontSize: "0.9375rem", fontWeight: 600, lineHeight: 1.25 },
    h5: { fontSize: "0.8125rem", fontWeight: 600, lineHeight: 1.3 },
    h6: { fontSize: "0.75rem", fontWeight: 600, lineHeight: 1.3 },
    subtitle1: { fontSize: "0.8125rem", fontWeight: 500, lineHeight: 1.35 },
    subtitle2: { fontSize: "0.75rem", fontWeight: 500, lineHeight: 1.35 },
    body1: { fontSize: "0.8rem", fontWeight: 400, lineHeight: 1.45 },
    body2: { fontSize: "0.72rem", fontWeight: 400, lineHeight: 1.45 },
    button: { textTransform: "none", fontWeight: 600, fontSize: "0.78rem" },
    caption: { fontSize: "0.65rem", lineHeight: 1.3 },
    overline: { fontSize: "0.6rem", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 },
  },
  shape: { borderRadius: 4 },
  shadows: [
    "none",
    "0 1px 2px 0 rgb(0 0 0 / 0.04)",
    "0 1px 3px 0 rgb(0 0 0 / 0.06)",
    "0 2px 4px 0 rgb(0 0 0 / 0.06)",
    "0 4px 8px -1px rgb(0 0 0 / 0.08)",
    ...Array(20).fill("0 4px 8px -1px rgb(0 0 0 / 0.08)"),
  ],
  // Compact layout
  layoutConfig: {
    drawerWidth: 230,
    headerHeight: 48,
    contentPadding: 1.5,
    contentPaddingTop: 1,
    sidebarLogoHeight: 48,
  },
  // Custom eliton colors for sidebar
  eliton: {
    sidebarBg: eliton.dark,
    sidebarText: "rgba(255,255,255,0.85)",
    sidebarTextMuted: "rgba(255,255,255,0.5)",
    sidebarHover: "rgba(255,255,255,0.08)",
    sidebarActive: eliton.red,
    sidebarActiveBg: "rgba(229, 0, 25, 0.15)",
    sidebarDivider: "rgba(255,255,255,0.08)",
    accentRed: eliton.red,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: eliton.gray100 },
        "*::-webkit-scrollbar": { width: 6, height: 6 },
        "*::-webkit-scrollbar-track": { background: "transparent" },
        "*::-webkit-scrollbar-thumb": { background: eliton.dark200, borderRadius: 3 },
        "*::-webkit-scrollbar-thumb:hover": { background: eliton.dark300 },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 4,
          padding: "5px 14px",
          fontSize: "0.78rem",
          minHeight: 32,
        },
        sizeSmall: { padding: "3px 10px", fontSize: "0.72rem", minHeight: 28 },
        contained: {
          boxShadow: "none",
          "&:hover": { boxShadow: "0 2px 4px rgba(0,0,0,0.12)" },
        },
        containedPrimary: {
          backgroundColor: eliton.red,
          "&:hover": { backgroundColor: eliton.redDark },
        },
        outlined: {
          borderColor: eliton.gray300,
          "&:hover": { borderColor: eliton.dark200, backgroundColor: eliton.gray50 },
        },
        outlinedPrimary: {
          borderColor: "#fca5a5",
          color: eliton.red,
          "&:hover": { borderColor: eliton.red, backgroundColor: "#fef2f2" },
        },
      },
      defaultProps: { disableElevation: true },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          border: `1px solid ${eliton.gray300}`,
          borderRadius: 6,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
        elevation1: { boxShadow: "0 1px 2px rgba(0,0,0,0.05)" },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 4, height: 24 },
        sizeSmall: { height: 20, fontSize: "0.65rem" },
        filled: { backgroundColor: eliton.gray200, color: eliton.dark500 },
        colorPrimary: { backgroundColor: "#fee2e2", color: "#991b1b" },
        colorSuccess: { backgroundColor: "#dcfce7", color: "#166534" },
        colorError: { backgroundColor: "#fee2e2", color: "#991b1b" },
        colorWarning: { backgroundColor: "#fef3c7", color: "#92400e" },
        colorInfo: { backgroundColor: "#e1f5fe", color: "#01579b" },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: eliton.gray200, padding: "6px 10px", fontSize: "0.78rem" },
        head: {
          fontWeight: 700,
          backgroundColor: eliton.gray100,
          color: eliton.dark500,
          textTransform: "uppercase",
          fontSize: "0.65rem",
          letterSpacing: "0.04em",
          padding: "6px 10px",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: eliton.white,
          color: eliton.dark,
          boxShadow: "none",
          borderBottom: `1px solid ${eliton.gray300}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: "none",
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          margin: "1px 6px",
          minHeight: 34,
          padding: "4px 10px",
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: { root: { minWidth: 30, color: "inherit" } },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: `1px solid ${eliton.gray300}`,
          borderRadius: 6,
          fontSize: "0.78rem",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: eliton.gray100,
            borderBottom: `1px solid ${eliton.gray300}`,
            minHeight: "36px !important",
            maxHeight: "36px !important",
          },
          "& .MuiDataGrid-columnHeader": { height: "36px !important" },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
            fontSize: "0.65rem",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: eliton.dark500,
          },
          "& .MuiDataGrid-row": {
            minHeight: "36px !important",
            maxHeight: "36px !important",
          },
          "& .MuiDataGrid-row:hover": { backgroundColor: eliton.gray50 },
          "& .MuiDataGrid-cell": {
            borderColor: eliton.gray200,
            padding: "4px 10px",
            display: "flex",
            alignItems: "center",
          },
          "& .MuiDataGrid-footerContainer": {
            minHeight: 38,
            borderTop: `1px solid ${eliton.gray300}`,
          },
        },
      },
      defaultProps: { density: "compact", rowHeight: 36, columnHeaderHeight: 36 },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 4,
            fontSize: "0.8rem",
            "& fieldset": { borderColor: eliton.gray300 },
            "&:hover fieldset": { borderColor: eliton.dark200 },
            "&.Mui-focused fieldset": { borderColor: eliton.red },
          },
          "& .MuiOutlinedInput-input": { padding: "7px 12px" },
        },
      },
      defaultProps: { size: "small" },
    },
    MuiInputBase: {
      styleOverrides: {
        sizeSmall: { fontSize: "0.78rem" },
      },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 8 } },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 4, padding: "6px 14px", fontSize: "0.78rem" },
        standardSuccess: { backgroundColor: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0" },
        standardError: { backgroundColor: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca" },
        standardWarning: { backgroundColor: "#fffbeb", color: "#92400e", border: "1px solid #fde68a" },
        standardInfo: { backgroundColor: "#e1f5fe", color: "#01579b", border: "1px solid #b3e5fc" },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { width: 28, height: 28, fontSize: "0.78rem" },
        colorDefault: { backgroundColor: eliton.red, color: eliton.white },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: "none", minHeight: 38, padding: "6px 14px", fontSize: "0.78rem", fontWeight: 600 },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 38 },
        indicator: { backgroundColor: eliton.red },
      },
    },
    MuiToolbar: {
      styleOverrides: { root: { minHeight: "48px !important" } },
    },
    MuiIconButton: {
      styleOverrides: { sizeSmall: { padding: 4 } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { fontSize: "0.7rem", borderRadius: 4, padding: "4px 10px", backgroundColor: eliton.dark },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { borderRadius: 6, border: `1px solid ${eliton.gray300}`, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { fontSize: "0.78rem", minHeight: 34, padding: "4px 14px" },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: eliton.gray300 } },
    },
    MuiSelect: {
      styleOverrides: {
        select: { padding: "7px 12px", fontSize: "0.78rem" },
      },
    },
    MuiBadge: {
      styleOverrides: {
        colorPrimary: { backgroundColor: eliton.red },
      },
    },
  },
})

// Legacy exports for backward compatibility
export const flexflowTheme = erpMinimalTheme
export const flexflowDarkTheme = erpMinimalTheme

export default erpMinimalTheme
